import { apiSlice } from '../../../store/api/apiSlice';

/** One entity match. The server sends identity only — no counts, no display joins. */
export interface AdminSearchHit {
  id: string;
  title: string;
  subtitle: string;
}

/**
 * A group of hits for one entity type. `type` is the server's stable key; the
 * frontend owns the mapping from type to route, so routing stays in the app.
 */
export interface AdminSearchGroup {
  type: string;
  label: string;
  results: AdminSearchHit[];
}

export interface AdminSearchResponse {
  query: string;
  groups: AdminSearchGroup[];
}

export interface AdminSearchParams {
  q: string;
  limit?: number;
}

/**
 * The one place the "is this term the same search as that term" rule is defined.
 * The cache key folds case and trims whitespace so retyping "Dana" as "dana"
 * reuses the earlier fetch instead of round-tripping again — but a cache hit then
 * means the response comes back carrying whatever casing was sent on the *first*
 * request for that key, not the caller's latest casing. Any code that compares an
 * incoming query against a response's echoed `query` field has to fold the same
 * way, or a same-search-different-casing edit reads as "this response is for a
 * different search" and the results it already has get thrown away for nothing.
 */
export const normalizeAdminSearchTerm = (term: string): string => term.trim().toLowerCase();

export const adminSearchApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    searchAdmin: builder.query<AdminSearchResponse, AdminSearchParams>({
      query: ({ q, limit }) => ({
        url: '/admin/search',
        params: limit ? { q, limit } : { q },
      }),
      // Results are keyed by query string, so RTK Query caches each term and
      // re-typing a term the palette has already seen resolves from cache.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${normalizeAdminSearchTerm(queryArgs.q)}|${queryArgs.limit ?? ''})`,
    }),
  }),
});

export const { useSearchAdminQuery, useLazySearchAdminQuery } = adminSearchApi;
